import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { usePendingCount } from "../hooks/usePendingCount";
import { can } from "@family/shared";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { NotificationToggle } from "./NotificationToggle";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

export function Drawer({ open, onClose }: DrawerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useAuth();

  const meQuery = useMe();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const role = resolveMembership(meQuery.data)?.role;
  const slug = resolveMembership(meQuery.data)?.familySlug;
  // The editorial welcome retires from the home page after the family's
  // welcome window; this link brings it back on demand.
  const hasWelcome = resolveMembership(meQuery.data)?.homeLayout === "both";
  const canViewMembers = role ? can(role, "members:view") : false;
  const canEditTree = role ? can(role, "tree:edit") : false;
  const canInvite = role ? can(role, "members:invite") : false;
  const canEditSettings = role ? can(role, "settings:edit") : false;
  const canViewSessions = role ? can(role, "sessions:view") : false;
  const canViewStats = role ? can(role, "stats:view") : false;
  const canViewEvents = role ? can(role, "events:view") : false;
  const pendingCount = usePendingCount();

  const testMembershipQuery = useQuery<{ isMember: boolean }>({
    queryKey: ["test-membership", slug],
    queryFn: () => apiFetch(`/families/${slug}/testing/membership`),
    enabled: Boolean(slug),
  });
  const canViewTesting = testMembershipQuery.data?.isMember ?? false;

  async function handleSignOut() {
    onClose();
    queryClient.clear();
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <>
      <div
        className={`drawer-backdrop ${open ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`drawer ${open ? "is-open" : ""}`}
        role="dialog"
        aria-label="Navigation"
        aria-modal={open}
      >
        <div className="drawer__header">
          <span className="drawer__title">Menu</span>
          <button
            className="app-header__icon-btn"
            onClick={onClose}
            aria-label="Close menu"
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <nav className="drawer__nav">
          <NavLink
            to="/"
            end
            onClick={onClose}
            className={({ isActive }) =>
              `drawer__link ${isActive ? "is-active" : ""}`
            }
          >
            Timeline
          </NavLink>

          {hasWelcome && (
            <Link to="/?welcome=1" onClick={onClose} className="drawer__link">
              About this site
            </Link>
          )}

          <NavLink
            to="/how-to"
            onClick={onClose}
            className={({ isActive }) =>
              `drawer__link ${isActive ? "is-active" : ""}`
            }
          >
            How to
          </NavLink>

          <NavLink
            to="/profile"
            onClick={onClose}
            className={({ isActive }) =>
              `drawer__link ${isActive ? "is-active" : ""}`
            }
          >
            My profile
          </NavLink>

          <NavLink
            to="/tree"
            onClick={onClose}
            className={({ isActive }) =>
              `drawer__link ${isActive ? "is-active" : ""}`
            }
          >
            Family tree
          </NavLink>

          {canViewEvents && (
            <NavLink
              to="/events"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Events
            </NavLink>
          )}

          <NavLink
            to="/news"
            onClick={onClose}
            className={({ isActive }) =>
              `drawer__link ${isActive ? "is-active" : ""}`
            }
          >
            News
          </NavLink>

          <NavLink
            to="/gallery"
            onClick={onClose}
            className={({ isActive }) =>
              `drawer__link ${isActive ? "is-active" : ""}`
            }
          >
            Photos &amp; Videos
          </NavLink>

          <NavLink
            to="/albums"
            onClick={onClose}
            className={({ isActive }) =>
              `drawer__link ${isActive ? "is-active" : ""}`
            }
          >
            Albums
          </NavLink>

          {canViewMembers && (
            <NavLink
              to="/members"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Members
              {pendingCount > 0 && (
                <span
                  className="drawer__link-badge"
                  title={`${pendingCount} pending member${pendingCount === 1 ? "" : "s"} to approve`}
                >
                  {pendingCount > 9 ? "9+" : pendingCount}
                </span>
              )}
            </NavLink>
          )}

          {canEditTree && (
            <NavLink
              to="/tree-audit"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Tree audit
            </NavLink>
          )}

          {canInvite && (
            <NavLink
              to="/invites"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Invites
            </NavLink>
          )}

          {canEditSettings && (
            <NavLink
              to="/settings"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Settings
            </NavLink>
          )}

          {canViewSessions && (
            <NavLink
              to="/sessions"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Sessions
            </NavLink>
          )}

          {canViewStats && (
            <NavLink
              to="/stats"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Stats
            </NavLink>
          )}

          <NavLink
            to="/issues"
            onClick={onClose}
            className={({ isActive }) =>
              `drawer__link ${isActive ? "is-active" : ""}`
            }
          >
            Feedback
          </NavLink>

          {role && can(role, "members:approve") && (
            <NavLink
              to="/release-notes"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Release notes
            </NavLink>
          )}

          {canViewTesting && (
            <NavLink
              to="/testing"
              onClick={onClose}
              className={({ isActive }) =>
                `drawer__link ${isActive ? "is-active" : ""}`
              }
            >
              Testing
            </NavLink>
          )}
          <div className="drawer__link" style={{ paddingTop: 6, paddingBottom: 6 }}>
            <NotificationToggle />
          </div>
        </nav>

        <div className="drawer__footer">
          {meQuery.data?.user && (
            <div className="drawer__user">
              <div className="drawer__user-name">
                {meQuery.data.user.displayName ?? "You"}
              </div>
              <div className="drawer__user-meta text-subtle">
                {meQuery.data.user.email ?? meQuery.data.user.phone}
              </div>
            </div>
          )}
          <button className="btn-secondary" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
